import { Text, UseDisclosureReturn, VStack } from "@chakra-ui/react";
import { returnFinancialEndYear } from "@syla/shared/helpers/financialYear";
import { ParcelMethod } from "@syla/shared/types/models/AccountBase";
import React from "react";
import { useForm } from "react-hook-form";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { checkStatusAndDoPendingWork } from "../../../../store/pendingWork";
import { useMutationUpdateAccount } from "../../../../store/useMutationUpdateAccount";
import { FormModal } from "../../../modals/FormModal";
import { ParcelMethodSelectorInputField } from "./ParcelMethodSelectorInputField";

type EditParcelMethodFormValues = {
  parcelMethods: ParcelMethod[];
};

export function EditParcelMethodModal({
  currentParcelMethods,
  disclosure,
}: {
  currentParcelMethods: ParcelMethod[];
  disclosure: UseDisclosureReturn;
}): React.ReactElement {
  const values = React.useMemo<EditParcelMethodFormValues>(
    () => ({
      parcelMethods: currentParcelMethods,
    }),
    [currentParcelMethods],
  );

  const form = useForm<EditParcelMethodFormValues>({
    values,
    mode: "onChange",
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const mutationUpdateAccount = useMutationUpdateAccount(accountId);

  const onSubmit = async (data: EditParcelMethodFormValues) => {
    const currentYearEnd = returnFinancialEndYear(new Date());

    const currentYearParcelMethod = data.parcelMethods.find(
      (method) => Number(method.endYear) === currentYearEnd,
    )?.method;

    await mutationUpdateAccount.mutateAsync({
      taxSettings: {
        parcelMethods: data.parcelMethods,
        ...(currentYearParcelMethod && {
          defaultParcelMethod: currentYearParcelMethod,
        }),
      },
    });

    checkStatusAndDoPendingWork();
  };

  return (
    <FormModal
      disclosure={disclosure}
      form={form}
      title="Parcel matching method"
      submitButtonText="Save changes"
      submitCondition="dirty"
      successTitle="Parcel method updated"
      errorTitle="Update failed"
      onSubmit={onSubmit}
    >
      <Text color="gray.600" mb={6}>
        Select the parcel matching method to use for each financial year.
      </Text>

      <VStack spacing={4}>
        {currentParcelMethods.map((financialYear, index) => {
          const helperText =
            index === 0 ?
              " All future years will also use this parcel method."
            : "";

          return (
            <ParcelMethodSelectorInputField
              form={form}
              name={`parcelMethods.${index}.method`}
              label={`FY ${financialYear.endYear}`}
              helperText={helperText}
              isRequired
              key={financialYear.startYear}
              buttonProps={{
                width: "full",
              }}
              popoverProps={{
                matchWidth: true,
              }}
            />
          );
        })}
      </VStack>
    </FormModal>
  );
}
